import Fuse from 'fuse.js';
import React, { forwardRef, useMemo, useRef, useState, } from 'react';
import styled from 'styled-components';
import { Box } from '../Box';
import { IconStrict } from '../IconStrict';
import { Field } from '../fields/Field';
import { Input, StyledFrontIcon } from '../fields/components/CommonInput';
import { useOnClickOutside } from '../hooks';
import { theme } from '../theme';
import { useUniqueId } from '../utils/id';
import { useControllableState } from '../utils/useControlledState';
import { SearchOptions } from './components/SearchOptions';
const defaultFuzzySearchOptions = {
    keys: [
        {
            name: 'label',
            weight: 0.6,
        },
        {
            name: 'tags',
            weight: 0.4,
        },
    ],
    findAllMatches: true,
    minMatchCharLength: 1,
    location: 0,
    threshold: 0.45,
    distance: 55,
};
export const SearchInput = forwardRef(function SearchInput({ id: idProp, name = 'search_input', className = '', placeholder, searchList, showIcon = false, renderAsTitle = false, value, onBlur, onFound, onNotFound, notFoundComponent, fallbackStyle, resultsRelativePosition = false, resultsBorder = true, enableFuzzySearch = false, fuzzySearchOptions, clearSearch, ...otherProps }, ref) {
    const wrapperRef = useRef(null);
    const id = useUniqueId(idProp);
    const [showOptions, setShowOptions] = useState(false);
    const [selectedValue, setSelectedValue] = useControllableState({
        initialState: null,
        stateProp: value,
    });
    const [searchQuery, setSearchQuery] = useState(null);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const selectedValueLabel = searchList.find((option) => option.label === selectedValue || option.value === selectedValue)?.label;
    const handleBlur = () => {
        if (selectedValue) {
            setSearchQuery(selectedValueLabel ?? null);
        }
        else if (!selectedValue) {
            setSearchQuery(null);
        }
    };
    useOnClickOutside({
        ref: wrapperRef,
        callback: () => {
            handleBlur();
            setShowOptions(false);
        },
    });
    const fuse = useMemo(() => {
        return new Fuse(searchList, {
            ...defaultFuzzySearchOptions,
            ...fuzzySearchOptions,
        });
    }, [searchList]);
    const filteredList = useMemo(() => {
        if (searchQuery === null || searchQuery === '') {
            return searchList;
        }
        if (enableFuzzySearch || !!fuzzySearchOptions) {
            return fuse.search(searchQuery).map(({ item }) => item);
        }
        return searchList.filter(({ label }) => label.toLowerCase().includes(searchQuery.toLocaleLowerCase()));
    }, [searchQuery, enableFuzzySearch, !!fuzzySearchOptions]);
    const getDisplayedInputText = () => {
        if (searchQuery !== null) {
            return searchQuery;
        }
        if (selectedValue !== null) {
            return selectedValueLabel || '';
        }
        return '';
    };
    const isSelected = selectedValue !== null;
    const displayedInputText = getDisplayedInputText();
    const updateSearchQuery = (query) => {
        setSearchQuery(query);
        setHighlightedIndex(-1);
        if (query === null) {
            setSelectedValue(null);
            setShowOptions(false);
        }
        else {
            setShowOptions(true);
        }
    };
    const handleClick = () => {
        setShowOptions(true);
        if (searchQuery !== null) {
            updateSearchQuery(searchQuery);
            setShowOptions(true);
        }
        else if (selectedValue !== null && searchQuery === null) {
            setSearchQuery(selectedValueLabel || null);
            setShowOptions(true);
        }
    };
    const handleInputChange = (event) => {
        const nextValue = event.currentTarget.value;
        updateSearchQuery(nextValue);
    };
    const handleSelect = (nextValue) => {
        updateSearchQuery(null);
        setSelectedValue(nextValue.label);
        onFound(nextValue.value);
    };
    const handleClearSearch = () => {
        updateSearchQuery(null);
        setSelectedValue(null);
        onFound('');
    };
    const handleCaretClick = () => {
        setShowOptions(!showOptions);
    };
    const handleKeyDown = (event) => {
        if (event.key === 'Enter' && highlightedIndex !== -1) {
            event.preventDefault();
            const focusedItem = filteredList[highlightedIndex];
            handleSelect(focusedItem);
        }
        else if (event.key === 'ArrowDown') {
            event.preventDefault();
            const nextIndex = (highlightedIndex + 1) % filteredList.length;
            setHighlightedIndex(nextIndex);
        }
        else if (event.key === 'ArrowUp') {
            event.preventDefault();
            const prevIndex = (highlightedIndex - 1 + filteredList.length) % filteredList.length;
            setHighlightedIndex(prevIndex);
        }
    };
    const showClearSearchButton = !!clearSearch && (!!value || !!selectedValue || !!searchQuery);
    return (React.createElement(Wrapper, { ref: wrapperRef },
        React.createElement(Field, { className: className, renderAsTitle: renderAsTitle, htmlFor: id, ...otherProps },
            React.createElement(Box, { flex: true, alignItems: "center", justifyContent: "flex-start" },
                showIcon && React.createElement(StyledFrontIcon, { render: "search", color: "sesame" }),
                React.createElement(Input, { id: id, name: name, ref: ref, placeholder: placeholder, "$error": otherProps.error, "$frontIcon": showIcon ? 'search' : undefined, "$fallbackStyle": fallbackStyle, autoComplete: "off", value: displayedInputText, onFocus: handleClick, onChange: handleInputChange, selected: isSelected, onClick: handleClick, onKeyDown: handleKeyDown, onBlur: (e) => {
                        onBlur?.(e);
                    } }),
                React.createElement(Icons, { flex: true, alignItems: "center", gap: "8px", "$clearSearch": showClearSearchButton },
                    showClearSearchButton && (React.createElement(IconStrict, { type: "button", render: "plus", rotate: 45, iconColor: "marzipan", title: "Clear search", handleClick: handleClearSearch, size: 24 })),
                    React.createElement(Line, null),
                    React.createElement(IconStrict, { type: "button", render: "caret", iconColor: "marzipan", rotate: showOptions ? 180 : 0, handleClick: handleCaretClick, size: 24 }))),
            showOptions && (React.createElement(SearchOptions, { displayedList: filteredList, selectedValue: selectedValue, highlightedIndex: highlightedIndex, setHighlightedIndex: setHighlightedIndex, onKeyDown: handleKeyDown, searchTerm: searchQuery || '', onSelect: handleSelect, positionRelative: resultsRelativePosition, resultsBorder: resultsBorder, onNotFound: onNotFound, notFoundComponent: notFoundComponent?.(searchQuery ?? '') })))));
});
const Wrapper = styled(Box) `
  position: relative;
`;
const Line = styled(Box) `
  background: ${theme.colors.oatmeal};
  height: 24px;
  width: 1px;
`;
export const Icons = styled(Box) `
  position: relative;
  right: ${({ $clearSearch }) => ($clearSearch ? '80px' : '48px')};
  margin-right: ${({ $clearSearch }) => ($clearSearch ? '-80px' : '-48px')};
`;
//# sourceMappingURL=SearchInput.js.map